/**
 * 카탈로그 — 공통코드가 아닌 선택 목록
 * ---------------------------------------------------------------------------
 * 제품·모듈·계량 항목은 각자의 표(platform.product, platform.module,
 * metering.metric_definition)가 주인이다. 공통코드로 복사해 두면 제품을 하나
 * 등록하고 공통코드에 넣는 것을 잊었을 때 요금제 화면에서 그 제품이 사라진다.
 * 그래서 이것들만 따로 서버에서 읽어 온다.
 *
 * 통합관리자 화면에서만 쓴다(`/admin/catalog`).
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient, type NormalizedError } from './api-client';

export interface CatalogMetric {
  value: string;
  label: string;
  /** BYTES · COUNT · REQUESTS · SECONDS */
  unit: string;
  /** 어느 제품이 올리는 값인가 */
  productCode: string;
  /** SUM 이면 기간 합계, 그 외(MAX·LAST·AVG)는 현재 상태 */
  aggregation: string;
  description: string | null;
}

export interface Catalog {
  products: Array<{ value: string; label: string }>;
  modules: Array<{ value: string; label: string; productCode: string; description: string | null }>;
  metrics: CatalogMetric[];
}

const EMPTY: Catalog = { products: [], modules: [], metrics: [] };

export function useCatalog() {
  const query = useQuery<Catalog, NormalizedError>({
    queryKey: ['admin', 'catalog'],
    // 제품·모듈이 늘어나는 일은 배포를 동반한다. 화면을 옮길 때마다 받지 않는다.
    staleTime: 10 * 60 * 1000,
    queryFn: async () => (await apiClient.get<Catalog>('/admin/catalog')).data,
  });

  const data = query.data ?? EMPTY;

  return {
    ...data,
    loading: query.isLoading,
    /** 계량 항목의 단위. 용량은 GB 로 받아 바이트로 저장하는 칸이 있다. */
    unitOf: (metricKey: string) =>
      data.metrics.find((m) => m.value === metricKey)?.unit ?? 'COUNT',
    labelOf: (metricKey: string) =>
      data.metrics.find((m) => m.value === metricKey)?.label ?? metricKey,
    metricOf: (metricKey: string) => data.metrics.find((m) => m.value === metricKey),
    moduleLabel: (moduleCode: string) =>
      data.modules.find((m) => m.value === moduleCode)?.label ?? moduleCode,
  };
}
